import { Skeleton } from '@slal/ui/animations';
import { Article, Heading, P } from '@slal/ui/typography';
import { useSelectedInvestmentPathway } from '~/hooks';
import { InvestmentPathwayOption } from '~/types';
import { InvestmentPathwayProps } from './types';

export const InvestmentPathwaySummary = ({ fundCode }: InvestmentPathwayProps) => {
    const { investmentPathway, loading } = useSelectedInvestmentPathway(fundCode);

    if (!fundCode) return null;

    const getSelectedPathwayContent = (fundCode: InvestmentPathwayOption) => {
        switch (fundCode) {
            case InvestmentPathwayOption.OPTION_1:
                return (
                    <Article>
                        <P>
                            By choosing the Standard Life Investment Pathway 1 Fund, you won't be planning to access
                            your money in the next five years.
                        </P>
                        <P>
                            It's designed for the long-term, so more than five years, to aim to grow your money. You
                            might see the value of your money going down as well as up a lot in the short term
                        </P>
                    </Article>
                );

            case InvestmentPathwayOption.OPTION_2:
                return (
                    <Article>
                        <P>
                            By choosing the Standard Life Investment Pathway 2 Fund, you'll be planning to buy a
                            guaranteed income (annuity) in the next five years.
                        </P>
                        <P>
                            It's designed to help lessen the impact of changes in annuity rates so when you come to buy
                            a guaranteed income, you're less likely to be affected by changes in the cost of buying one.
                            You may still see the value go down as well as up, sometimes by a lot.
                        </P>
                    </Article>
                );

            case InvestmentPathwayOption.OPTION_3:
                return (
                    <Article>
                        <P>
                            By choosing the Standard Life Investment Pathway 3 Fund, you'll be planning to start
                            accessing your money within the next five years and for it to last longer.
                        </P>
                        <P>
                            It's designed for the long-term, so more than five years, to aim to grow your money while
                            offering some stability. You may still see the value of your money going down as well as up
                            in the short term.
                        </P>
                    </Article>
                );

            case InvestmentPathwayOption.OPTION_4:
                return (
                    <Article>
                        <P>
                            By choosing the Standard Life Investment Pathway 4 Fund, you'll be planning to take all of
                            your money in the next five years.
                        </P>
                        <P>
                            It's been designed with this in mind so you could expect it to aim for relatively stable
                            returns. Its value may still go down as well as up.
                        </P>
                    </Article>
                );

            default:
                return null;
        }
    };

    const pathwayContent = getSelectedPathwayContent(fundCode as InvestmentPathwayOption);

    return (
        <Article>
            <Heading level={4}>{loading ? <Skeleton width="60%" /> : investmentPathway?.fundName}</Heading>
            <P>{pathwayContent}</P>
            <P markdown>
                There are other providers who offer a Pathway for this option. MoneyHelper can help you compare your
                options with their [comparison
                tool](https://www.moneyhelper.org.uk/en/pensions-and-retirement/taking-your-pension/drawdown-investment-pathways).
            </P>
        </Article>
    );
};
